import { useState, useCallback } from "react";

/**
 * useCart - Hook para gerenciamento completo do carrinho
 * Responsabilidades:
 * - Adicionar/remover itens
 * - Atualizar quantidades
 * - Calcular totais
 * - Suporte a modificadores
 * - Desconto fixo aplicável ao total
 */
export function useCart() {
  const [cart, setCart] = useState([]);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountType, setDiscountType] = useState("fixed");
  const [discountValue, setDiscountValue] = useState(0);
  const [discountReason, setDiscountReason] = useState("");

  // Adicionar produto simples (sem modificadores)
  const addItem = useCallback((product) => {
    setCart(prev => {
      const existing = prev.find(i => i.product_id === product.id && !i.modifier_key);
      if (existing) {
        return prev.map(i =>
          (i.product_id === product.id && !i.modifier_key)
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [...prev, {
        product_id: product.id,
        product_name: product.name,
        unit_price: parseFloat(product.price) || 0,
        quantity: 1
      }];
    });
  }, []);

  // Adicionar produto com modificadores
  const addItemWithModifiers = useCallback(({ product, modifiers, selected_variations, extra_price, total_price }) => {
    const modifierKey = modifiers.map(m =>
      `${m.group_id}:${m.options.map(o => `${o.id}x${o.quantity || 1}`).sort().join(",")}`
    ).join("|");

    const modifierDescription = (selected_variations || []).map(v =>
      (v.quantity || 1) > 1 ? `${v.quantity}× ${v.name}` : v.name
    ).join(", ") || modifiers.map(m =>
      m.options.map(o => o.name).join(", ")
    ).join(" / ");

    setCart(prev => {
      const existing = prev.find(i =>
        i.product_id === product.id &&
        i.modifier_key === modifierKey
      );

      if (existing) {
        return prev.map(i =>
          (i.product_id === product.id && i.modifier_key === modifierKey)
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }

      return [...prev, {
        product_id: product.id,
        product_name: product.name,
        unit_price: parseFloat(total_price) || parseFloat(product.price) || 0,
        base_price: parseFloat(product.price) || 0,
        extra_price: parseFloat(extra_price) || 0,
        quantity: 1,
        modifiers,
        selected_variations: selected_variations || [],
        modifier_key: modifierKey,
        modifier_description: modifierDescription
      }];
    });
  }, []);

  // Atualizar quantidade
  const updateQuantity = useCallback((productId, delta, modifierKey = null) => {
    setCart(prev => prev.map(i => {
      const matches = modifierKey
        ? (i.product_id === productId && i.modifier_key === modifierKey)
        : (i.product_id === productId && !i.modifier_key);
      if (!matches) return i;
      const newQty = i.quantity + delta;
      return { ...i, quantity: newQty };
    }).filter(i => i.quantity > 0));
  }, []);

  // Atualizar observação de um item
  const updateItemNotes = useCallback((productId, modifierKey, notes) => {
    setCart(prev => prev.map(i => {
      const matches = modifierKey
        ? (i.product_id === productId && i.modifier_key === modifierKey)
        : (i.product_id === productId && !i.modifier_key);
      return matches ? { ...i, notes } : i;
    }));
  }, []);

  // Remover item
  const removeItem = useCallback((productId, modifierKey = null) => {
    setCart(prev => prev.filter(i => {
      if (modifierKey) return !(i.product_id === productId && i.modifier_key === modifierKey);
      return !(i.product_id === productId && !i.modifier_key);
    }));
  }, []);

  // Limpar carrinho (também reseta desconto)
  const clearCart = useCallback(() => {
    setCart([]);
    setDiscountAmount(0);
    setDiscountType("fixed");
    setDiscountValue(0);
    setDiscountReason("");
  }, []);

  // Calcular subtotal
  const subtotal = cart.reduce((sum, item) => sum + (item.quantity * (item.unit_price || 0)), 0);

  // Total após desconto
  const total = Math.max(0, subtotal - (discountAmount || 0));

  // Calcular total de itens
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Verificar se produto está no carrinho (por product_id + modifier_key opcional)
  const getCartItem = useCallback((productId, modifierKey = null) => {
    if (modifierKey) {
      return cart.find(i => i.product_id === productId && i.modifier_key === modifierKey);
    }
    return cart.find(i => i.product_id === productId && !i.modifier_key);
  }, [cart]);

  // Soma das quantidades de um produto em todas as suas variantes no carrinho
  const getCartTotalQty = useCallback((productId) => {
    return cart.filter(i => i.product_id === productId).reduce((sum, i) => sum + i.quantity, 0);
  }, [cart]);

  // Formatar itens para envio ao backend
  const formatForOrder = useCallback(() => {
    return cart.map(item => ({
      product_id: item.product_id,
      product_name: item.product_name,
      quantity: item.quantity,
      unit_price: item.unit_price,
      modifiers: item.modifiers || null,
      modifier_description: item.modifier_description || null,
      selected_variations: item.selected_variations || null,
      notes: item.notes || null,
    }));
  }, [cart]);

  return {
    cart,
    setCart,
    addItem,
    addItemWithModifiers,
    updateQuantity,
    updateItemNotes,
    removeItem,
    clearCart,
    subtotal,
    total,
    discountAmount,
    setDiscountAmount,
    discountType,
    setDiscountType,
    discountValue,
    setDiscountValue,
    discountReason,
    setDiscountReason,
    itemCount,
    getCartItem,
    getCartTotalQty,
    formatForOrder,
    isEmpty: cart.length === 0
  };
}
